/**
 * Tickets 数据获取和自动刷新 Hook
 * 负责获取订单列表、自动刷新、管理加载状态
 * 使用 createSharedRoot 实现多组件共享状态
 */
import { createSignal, createMemo, onMount, onCleanup } from 'solid-js';
import { createSharedRoot } from '@bz/request';

import { statusFilters } from '../shared';
import { TICKETS_CONFIG } from '../config';

type BetSelection = ApiResponse<'getUserTicketsPage'>['betSelections'][number];
type TicketsData = Awaited<ReturnType<typeof get.getUserTicketsPage>>;

/**
 * 共享的 Tickets 数据 Store
 * 只管理数据状态，不包含生命周期逻辑
 */
const useTicketsStore = createSharedRoot(() => {
  const [ticketsData, setTicketsData] = createSignal<TicketsData | undefined>(undefined);
  const [isFirstLoading, setIsFirstLoading] = createSignal(true);

  // 获取 tickets 的函数
  const fetchTickets = async () => {
    const filterValue = statusFilters.find((item) => item.slug === 'open-bets')?.value;
    try {
      const res = await getOnce.getUserTicketsPage({
        pageSize: String(TICKETS_CONFIG.PAGE_SIZE),
        pageIndex: '1',
        betType: TICKETS_CONFIG.BET_TYPES as any,
        needTotalCount: 'true',
        orderDirection: 'DESC',
        orderBy: 'createTime',
        settledStatus: filterValue as any,
      });
      setTicketsData(res);
      setIsFirstLoading(false);
    } catch (err) {
      console.error('获取 tickets 失败', err);
      setIsFirstLoading(false);
    }
  };

  // 构建 selections Map，方便通过 selectionNo 查找
  const selectionsMap = createMemo(() => {
    const data = ticketsData();
    if (!data) return new Map<string, BetSelection>();
    return data.betSelections.reduce((acc, item) => {
      acc.set(item.selectionNo, item);
      return acc;
    }, new Map<string, BetSelection>());
  });

  // Open Bets 订单数量
  const openBetsCount = createMemo(() => ticketsData()?.betTickets?.length ?? 0);

  const [openBetCounts, setOpenBetCounts] = createSignal(0);

  const getOpenBetCount = async () => {
    try {
      const res = await getOnce.getUserTicketsCount({ settledStatus: [0] });
      const count = Number(res) || 0;
      setOpenBetCounts(count);
      return count;
    } catch (err) {
      console.error('获取 open bet count 失败', err);
    }
  };

  return {
    ticketsData,
    isFirstLoading,
    selectionsMap,
    openBetsCount,
    fetchTickets,
    // 新增 后续抽空整理一下这块
    getOpenBetCount,
    openBetCounts,
  };
});

/**
 * useTicketsData Hook
 * 在组件中使用，包含生命周期管理（自动刷新）
 */
export const useTicketsData = () => {
  const store = useTicketsStore();
  return store;
};

/**
 * useTicketsDataWithPolling Hook
 * 带有自动刷新逻辑，适用于需要定时刷新的组件
 * 在组件 mount 时开始刷新，unmount 时停止
 */
export const useTicketsDataWithPolling = () => {
  const store = useTicketsStore();

  // 初始化时加载数据
  onMount(() => {
    store.fetchTickets();
  });

  // 定时刷新 tickets
  let ticketsRefreshTimer: NodeJS.Timeout | undefined;

  onMount(() => {
    ticketsRefreshTimer = setInterval(() => {
      store.fetchTickets();
    }, TICKETS_CONFIG.REFRESH_INTERVAL);
  });

  onCleanup(() => {
    if (ticketsRefreshTimer) {
      clearInterval(ticketsRefreshTimer);
    }
  });

  return {
    ticketsData: store.ticketsData,
    isFirstLoading: store.isFirstLoading,
    selectionsMap: store.selectionsMap,
    openBetsCount: store.openBetsCount,
    refetch: store.fetchTickets,
  };
};
